import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes, css } from 'styled-components';
import { useThemeStore } from '../../store/themeStore';
import { ThemeType } from '../../types/command';
import ThemeSelector from '../ThemeSelector';
import LanguageSelector from '../LanguageSelector';
import SEO from '../SEO';

// Ana konteynır - Minimal arka plan deseni
const HomeContainer = styled.div<{ theme: ThemeType }>`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  position: relative;
  background-color: ${({ theme }) => 
    theme === 'ubuntu' ? '#300A24' : 
    theme === 'powershell' ? '#012456' : 
    '#0C0C0C'};
  overflow: hidden;
  pointer-events: none; /* Tüm alanı tıklanamaz yaptım */
  user-select: none; /* Metinlerin seçilebilirliğini engelliyorum */
  cursor: default; /* Varsayılan imleci göstermiyorum */
  
  /* Minimal arka plan deseni */
  background-image: ${({ theme }) => 
    theme === 'ubuntu' ? 
      'radial-gradient(circle at 10% 20%, rgba(233, 84, 32, 0.03) 0%, transparent 20%), ' +
      'radial-gradient(circle at 90% 80%, rgba(233, 84, 32, 0.03) 0%, transparent 20%)' : 
    theme === 'powershell' ? 
      'radial-gradient(circle at 10% 20%, rgba(0, 150, 255, 0.03) 0%, transparent 20%), ' +
      'radial-gradient(circle at 90% 80%, rgba(0, 150, 255, 0.03) 0%, transparent 20%)' : 
      'radial-gradient(circle at 10% 20%, rgba(100, 100, 100, 0.03) 0%, transparent 20%), ' +
      'radial-gradient(circle at 90% 80%, rgba(100, 100, 100, 0.03) 0%, transparent 20%)'
  };
  
  * {
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
  }
`;

// Selectors konteyner animasyonu
const fadeInTop = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Tema ve dil seçici konteyner
const SelectorsContainer = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  display: flex;
  gap: 15px;
  z-index: 100;
  animation: ${fadeInTop} 1s ease-out forwards;
  animation-delay: 1.5s;
  opacity: 0;
  
  @media (max-width: 768px) {
    top: 10px;
    right: 10px;
    gap: 10px;
  }
`;

// Floating animasyonu
const floatingEffect = keyframes`
  0% {
    transform: translate(0, 0) rotate(0deg);
  }
  25% {
    transform: translate(5px, 5px) rotate(0.5deg);
  }
  50% {
    transform: translate(0, 10px) rotate(0deg);
  }
  75% {
    transform: translate(-5px, 5px) rotate(-0.5deg);
  }
  100% {
    transform: translate(0, 0) rotate(0deg);
  }
`;

// Yazı ışıma animasyonu
const textGlow = keyframes`
  0%, 100% {
    text-shadow: 0 0 10px rgba(255, 255, 255, 0);
  }
  50% {
    text-shadow: 0 0 20px rgba(255, 255, 255, 0.6);
  }
`;

// İçerik konteynırı
const ContentContainer = styled.div`
  z-index: 10;
  text-align: center;
  max-width: 90%;
  width: 600px;
  padding: 40px;
  position: relative;
  
  /* Hafif arka plan efekti */
  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(120deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0) 50%, rgba(255,255,255,0.03) 100%);
    z-index: -1;
    pointer-events: none;
    border-radius: 20px;
  }
`;

// Logo animasyonu
const logoFade = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

// 3D dönüş animasyonu
const rotateSlow = keyframes`
  0% {
    transform: rotateY(0deg);
  }
  100% {
    transform: rotateY(360deg);
  }
`;

// Logo konteynırı
const LogoContainer = styled.div`
  margin-bottom: 40px;
  animation: ${logoFade} 1.5s ease-out forwards;
  position: relative;
  text-align: center; /* Konteyneri de merkezde hizala */
`;

// Logo hover efektini yeniden ekliyorum - Geliştirilmiş
const Logo = styled.h1<{ theme: ThemeType }>`
  font-size: 2.8rem;
  font-weight: 700;
  letter-spacing: 2px;
  color: ${({ theme }) => 
    theme === 'ubuntu' ? '#E95420' : 
    theme === 'powershell' ? '#0096FF' : 
    '#CCCCCC'};
  margin: 0;
  text-shadow: 0 0 10px ${({ theme }) => 
    theme === 'ubuntu' ? 'rgba(233, 84, 32, 0.3)' : 
    theme === 'powershell' ? 'rgba(0, 150, 255, 0.3)' : 
    'rgba(255, 255, 255, 0.3)'};
  animation: ${textGlow} 3s ease-in-out infinite;
  position: relative;
  font-family: ${({ theme }) => 
    theme === 'ubuntu' ? "'Ubuntu Mono', monospace" : 
    theme === 'powershell' ? "'Consolas', monospace" : 
    "'Consolas', monospace"};
  line-height: 1;
  text-align: center; /* Metni merkezde hizala */
  
  span {
    font-weight: 300;
    opacity: 0.8;
    animation: ${textGlow} 4s ease-in-out infinite;
    animation-delay: 1s;
  }
  
  /* Subtle dot animation */
  &:after {
    content: "";
    position: absolute;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background-color: ${({ theme }) => 
      theme === 'ubuntu' ? 'rgba(233, 84, 32, 0.7)' : 
      theme === 'powershell' ? 'rgba(0, 150, 255, 0.7)' : 
      'rgba(255, 255, 255, 0.7)'};
    bottom: 0;
    right: 0;
    animation: ${textGlow} 2s ease-in-out infinite;
  }
  
  /* Mobil responsive düzenlemeler */
  @media (max-width: 768px) {
    font-size: 2.2rem; /* Mobilde daha küçük font */
    letter-spacing: 1px;
    display: flex; /* Sadece mobilde flex kullan */
    align-items: center;
    justify-content: center;
    
    span {
      font-size: 2.2rem; /* Mobilde aynı boyutta kalmasını sağla */
    }
  }
`;

// Parıltı animasyonu
const sparkleAnimation = keyframes`
  0%, 100% {
    opacity: 0;
    transform: scale(0);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
`;

// Logo arka parıltısı
const LogoSparkle = styled.div<{ theme: ThemeType, $top: number, $left: number, $size: number, $delay: number }>`
  position: absolute;
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  border-radius: 50%;
  background: ${({ theme }) => 
    theme === 'ubuntu' ? '#E95420' : 
    theme === 'powershell' ? '#0096FF' : 
    '#CCCCCC'};
  top: ${props => props.$top}%;
  left: ${props => props.$left}%;
  opacity: 0;
  filter: blur(2px);
  animation: ${sparkleAnimation} 2s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
`;

// Logonun altındaki çizgi - Geliştirilmiş
const LogoUnderline = styled.div<{ theme: ThemeType }>`
  height: 3px;
  width: 60px;
  margin: 15px auto 0;
  background: ${({ theme }) => 
    theme === 'ubuntu' ? '#E95420' : 
    theme === 'powershell' ? '#0096FF' : 
    '#CCCCCC'};
  border-radius: 3px;
  position: relative;
  overflow: hidden;
  
  &:after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    animation: shimmer 2s infinite;
  }
  
  @keyframes shimmer {
    0% {
      left: -100%;
    }
    100% {
      left: 100%;
    }
  }
`;

// Tagline animasyonu
const taglineAnimation = keyframes`
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 0.8;
    transform: translateY(0);
  }
`;

// Tagline
const Tagline = styled.p<{ theme: ThemeType }>`
  font-size: 1.1rem;
  color: ${({ theme }) => 
    theme === 'ubuntu' ? '#FFFFFF' : 
    theme === 'powershell' ? '#FFFFFF' : 
    '#CCCCCC'};
  margin-bottom: 50px;
  opacity: 0;
  animation: ${taglineAnimation} 1.5s ease-out forwards;
  animation-delay: 0.6s;
  font-family: ${({ theme }) => 
    theme === 'ubuntu' ? "'Ubuntu Mono', monospace" : 
    theme === 'powershell' ? "'Consolas', monospace" : 
    "'Consolas', monospace"};
`;

// Seçenekler konteynırı animasyonu
const optionsAnimation = keyframes`
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Seçenekler konteynırı
const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  opacity: 0;
  animation: ${optionsAnimation} 1.5s ease-out forwards;
  animation-delay: 1s;
  will-change: opacity, transform;
`;

// Seçenek buttonları animasyonu
const optionHoverAnimation = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(8px);
  }
`;

// Buton arka plan animasyonu - hafif gradyan geçiş
const buttonGradient = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

// Seçenek buttonları için geliştirilmiş efektler
const OptionButton = styled.button<{ theme: ThemeType }>`
  background-color: transparent;
  border: 2px solid ${({ theme }) => 
    theme === 'ubuntu' ? 'rgba(233, 84, 32, 0.3)' : 
    theme === 'powershell' ? 'rgba(0, 150, 255, 0.3)' : 
    'rgba(120, 120, 120, 0.3)'};
  border-radius: 8px;
  padding: 18px 24px;
  font-size: 1.2rem;
  color: ${({ theme }) => 
    theme === 'ubuntu' ? '#FFFFFF' : 
    theme === 'powershell' ? '#FFFFFF' : 
    '#CCCCCC'};
  text-align: left;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;
  overflow: hidden;
  font-family: ${({ theme }) => 
    theme === 'ubuntu' ? "'Ubuntu Mono', monospace" : 
    theme === 'powershell' ? "'Consolas', monospace" : 
    "'Consolas', monospace"};
  pointer-events: auto; /* Butonları tıklanabilir yaptım */
  user-select: none; /* Metinlerin seçilebilirliğini engelliyorum */
  
  &:before {
    content: ">";
    position: absolute;
    left: 10px;
    opacity: 0;
    transition: all 0.3s ease;
  }
  
  &:after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: ${({ theme }) => 
      theme === 'ubuntu' ? 'linear-gradient(90deg, transparent, rgba(233, 84, 32, 0.1), transparent)' : 
      theme === 'powershell' ? 'linear-gradient(90deg, transparent, rgba(0, 150, 255, 0.1), transparent)' : 
      'linear-gradient(90deg, transparent, rgba(120, 120, 120, 0.1), transparent)'};
    transition: all 0.5s ease;
  }
  
  &:hover {
    background-image: ${({ theme }) => 
      theme === 'ubuntu' ? 'linear-gradient(120deg, rgba(233, 84, 32, 0.1), rgba(233, 84, 32, 0.05), rgba(233, 84, 32, 0.1))' : 
      theme === 'powershell' ? 'linear-gradient(120deg, rgba(0, 150, 255, 0.1), rgba(0, 150, 255, 0.05), rgba(0, 150, 255, 0.1))' : 
      'linear-gradient(120deg, rgba(70, 70, 70, 0.3), rgba(90, 90, 90, 0.2), rgba(70, 70, 70, 0.3))'};
    background-size: 200% 100%;
    animation: ${buttonGradient} 3s ease infinite;
    border-color: ${({ theme }) => 
      theme === 'ubuntu' ? 'rgba(233, 84, 32, 0.8)' : 
      theme === 'powershell' ? 'rgba(0, 150, 255, 0.8)' : 
      'rgba(120, 120, 120, 0.8)'};
    padding-left: 35px;
    transform: translateY(-3px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    
    &:before {
      opacity: 1;
      animation: ${optionHoverAnimation} 0.5s ease infinite alternate;
    }
    
    &:after {
      left: 100%;
    }
  }
  
  &:active {
    transform: scale(0.98) translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

// Pulsating dot animation
const pulsate = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.3;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.3;
  }
`;

// Terminal imleç animasyonu - gerçekçi davranış
const blink = keyframes`
  0%, 49% {
    opacity: 1;
    visibility: visible;
  }
  50%, 100% {
    opacity: 0;
    visibility: hidden;
  }
`;

// Geliştirilmiş Terminal Cursor - Gerçek imleç davranışı
const TerminalCursor = styled.span<{ theme: ThemeType }>`
  display: inline-block;
  width: 3px;
  height: 24px; /* Metnin boyutuna uygun yükseklik */
  background-color: ${({ theme }) => 
    theme === 'ubuntu' ? '#E95420' : 
    theme === 'powershell' ? '#0096FF' : 
    '#CCCCCC'};
  margin-left: 5px;
  animation: ${blink} 0.8s step-start infinite;
  vertical-align: middle; /* İmleci metin ile aynı hizada tutmak için */
  will-change: opacity, visibility;
  position: relative;
  box-shadow: 0 0 3px ${({ theme }) => 
    theme === 'ubuntu' ? 'rgba(233, 84, 32, 0.7)' : 
    theme === 'powershell' ? 'rgba(0, 150, 255, 0.7)' : 
    'rgba(204, 204, 204, 0.7)'};
  
  /* Mobil responsive düzenlemeler */
  @media (max-width: 768px) {
    height: 22px; /* Mobilde biraz daha küçük imleç */
  }
`;

// Footer içindeki easter egg yardımı
const EasterEggHint = styled.div<{ theme: ThemeType }>`
  margin-top: 10px;
  font-size: 0.75rem;
  opacity: 0.5;
  color: ${({ theme }) => 
    theme === 'ubuntu' ? '#E95420' : 
    theme === 'powershell' ? '#0096FF' : 
    '#CCCCCC'};
  transition: opacity 0.3s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

// Footer
const Footer = styled.footer<{ theme: ThemeType }>`
  position: absolute;
  bottom: 20px;
  text-align: center;
  font-size: 0.9rem;
  color: ${({ theme }) => 
    theme === 'ubuntu' ? 'rgba(255, 255, 255, 0.5)' : 
    theme === 'powershell' ? 'rgba(255, 255, 255, 0.5)' : 
    'rgba(204, 204, 204, 0.5)'};
  width: 100%;
  z-index: 10;
`;

// Matris karakter animasyonu - gerçekçi Matrix efekti
const matrixCharAnimation = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-20px);
  }
  3% {
    opacity: 1;
  }
  95% {
    opacity: 0.8;
  }
  100% {
    opacity: 0;
    transform: translateY(100vh);
  }
`;

// Matris karakter - gerçekçi Matrix stili
const MatrixChar = styled.div<{ $delay: number; $left: number; $speed: number; $size: number; $brightness?: number }>`
  position: absolute;
  top: -20px;
  left: ${props => props.$left}%;
  color: ${props => `rgba(0, 255, 0, ${props.$brightness || 0.8})`};
  font-family: 'Courier New', monospace;
  font-size: ${props => props.$size}px;
  font-weight: ${props => props.$brightness && props.$brightness > 0.9 ? 'bold' : 'normal'};
  opacity: 0;
  will-change: transform, opacity;
  animation: ${matrixCharAnimation} ${props => props.$speed}s linear infinite;
  animation-delay: ${props => props.$delay}s;
  z-index: 201;
  text-shadow: ${props => props.$brightness && props.$brightness > 0.9 ? '0 0 8px #0f0' : '0 0 3px #0f0'};
  pointer-events: none;
`;

// Matris modu konteyneri
const MatrixContainer = styled.div<{ $active: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 200;
  display: ${props => props.$active ? 'block' : 'none'};
  overflow: hidden;
  transition: opacity 0.5s ease;
  opacity: ${props => props.$active ? 1 : 0};
  pointer-events: none;
`;

// Dil ve tema butonları için container
const SettingsBar = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  display: flex;
  gap: 15px;
`;

// Ayarlar butonu başlığı
const SettingsLabel = styled.div<{ theme: ThemeType }>`
  color: ${({ theme }) => 
    theme === 'ubuntu' ? '#E95420' : 
    theme === 'powershell' ? '#0096FF' : 
    '#CCCCCC'};
  margin-right: 10px;
`;

// Ayarlar butonu
const SettingsButton = styled.button<{ theme: ThemeType }>`
  position: fixed;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${({ theme }) => 
    theme === 'ubuntu' ? 'rgba(233, 84, 32, 0.8)' : 
    theme === 'powershell' ? 'rgba(0, 150, 255, 0.8)' : 
    'rgba(100, 100, 100, 0.8)'};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  z-index: 1000;
  transition: all 0.3s ease;
  pointer-events: auto;
  
  &:hover {
    transform: rotate(30deg);
  }
`;

// Ayarlar popup
const SettingsPopup = styled.div<{ $isOpen: boolean; theme: ThemeType }>`
  position: fixed;
  top: 70px;
  right: 20px;
  width: 250px;
  background-color: rgba(10, 10, 10, 0.9);
  border-radius: 10px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.5);
  padding: 15px;
  z-index: 1000;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  border: 1px solid ${({ theme }) => 
    theme === 'ubuntu' ? 'rgba(233, 84, 32, 0.5)' : 
    theme === 'powershell' ? 'rgba(0, 150, 255, 0.5)' : 
    'rgba(100, 100, 100, 0.5)'};
  backdrop-filter: blur(10px);
  pointer-events: auto;
`;

// Ayarlar popup başlığı
const SettingsPopupTitle = styled.h3<{ theme: ThemeType }>`
  color: ${({ theme }) => 
    theme === 'ubuntu' ? '#E95420' : 
    theme === 'powershell' ? '#0096FF' : 
    '#CCCCCC'};
  margin: 0 0 15px 0;
  padding-bottom: 10px;
  border-bottom: 1px solid ${({ theme }) => 
    theme === 'ubuntu' ? 'rgba(233, 84, 32, 0.5)' : 
    theme === 'powershell' ? 'rgba(0, 150, 255, 0.5)' : 
    'rgba(100, 100, 100, 0.5)'};
  font-size: 1.1rem;
  text-align: center;
`;

// Ayarlar grubu
const SettingsGroup = styled.div`
  margin-bottom: 15px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

// Ayarlar grubu başlığı
const SettingsGroupTitle = styled.h4<{ theme: ThemeType }>`
  color: ${({ theme }) => 
    theme === 'ubuntu' ? '#E95420' : 
    theme === 'powershell' ? '#0096FF' : 
    '#CCCCCC'};
  margin: 0 0 10px 0;
  font-size: 0.9rem;
  opacity: 0.9;
`;

// Ayarlar select elementi
const SettingsSelect = styled.select<{ theme: ThemeType }>`
  width: 100%;
  padding: 8px 10px;
  border-radius: 5px;
  border: 1px solid ${({ theme }) => 
    theme === 'ubuntu' ? 'rgba(233, 84, 32, 0.5)' : 
    theme === 'powershell' ? 'rgba(0, 150, 255, 0.5)' : 
    'rgba(100, 100, 100, 0.5)'};
  background-color: rgba(20, 20, 20, 0.8);
  color: white;
  font-size: 0.9rem;
  outline: none;
  cursor: pointer;
  pointer-events: auto;
`;

interface HomePageProps {
  onOpenTerminal: () => void;
  onOpenServices: () => void;
}

// Ana sayfa bileşeni
const HomePageComponent: React.FC<HomePageProps> = ({ onOpenTerminal, onOpenServices }) => {
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useThemeStore();
  const [mounted, setMounted] = useState(false);
  
  // Ayarlar popup state'i
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  
  // Matrix modu için state
  const [matrixActive, setMatrixActive] = useState(false);
  const [matrixChars, setMatrixChars] = useState<Array<{ 
    x: number; 
    y: number; 
    char: string; 
    delay: number;
    speed: number;
    size: number;
    brightness?: number;
  }>>([]);
  
  useEffect(() => {
    setMounted(true);
    
    // Global CSS kuralı ekleyerek tüm sayfada imleç ve seçim işlemlerini engelleme
    const styleElement = document.createElement('style');
    styleElement.innerHTML = `
      * {
        user-select: none !important;
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
      }
      
      body, 
      p, 
      h1, 
      h2, 
      h3, 
      h4, 
      h5, 
      h6, 
      button, 
      div {
        cursor: default !important;
      }
      
      button, 
      a {
        cursor: pointer !important;
      }
      
      /* Logo imlecinin tıklamalardan etkilenmemesi için */
      .terminal-cursor-container * {
        pointer-events: none !important;
      }
      
      .terminal-cursor {
        display: inline-block !important;
        animation: terminalBlink 0.8s step-start infinite !important;
        vertical-align: middle !important;
        height: 24px !important;
      }
      
      @media (max-width: 768px) {
        .terminal-cursor {
          height: 22px !important;
        }
      }
      
      @keyframes terminalBlink {
        0%, 49% {
          opacity: 1;
          visibility: visible;
        }
        50%, 100% {
          opacity: 0;
          visibility: hidden;
        }
      }
    `;
    document.head.appendChild(styleElement);
    
    // Component unmount olduğunda stil elementini temizle
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);
  
  // Tema değiştirme fonksiyonu
  const handleThemeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setTheme(e.target.value as ThemeType);
  };
  
  // Dil değiştirme fonksiyonu
  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    i18n.changeLanguage(e.target.value);
  };
  
  // Matrix modu efektini etkinleştir - şerit halinde düşen Matrix efekti
  const activateMatrixMode = () => {
    // Matrix sembollerini oluşturuyorum - Japon alfabe karakterleri ve 0,1 rakamları
    const matrixSymbols = '日ﾊﾐﾋｰｳｼﾅﾓﾆｻﾜﾂｵﾘｱﾎﾃﾏｹﾒｴｶｷﾑﾕﾗｾﾈｽﾀﾇﾍ01'.split('');
    
    // Şeritler (sütunlar) halinde düzenleme - daha sık
    const columnCount = 35; // Sütun sayısını artırdım (daha sık yerleşim)
    const charsPerColumn = 30; // Her sütundaki karakter sayısı
    const chars = [];
    
    // Ekranın orta %70'lik kısmında sütunları daha sık yerleştir
    const screenWidthPercentage = 90; // Ekranın %90'ını kullan
    const startPosition = (100 - screenWidthPercentage) / 2; // Ortalamak için başlangıç pozisyonu
    
    // Her sütun için karakterleri oluştur
    for (let col = 0; col < columnCount; col++) {
      // Sütun pozisyonu - daha sık yerleşim için daraltılmış bir aralıkta
      const columnPosition = startPosition + ((col / columnCount) * screenWidthPercentage);
      
      // Her sütun için başlangıç gecikmesi (anında başlamaları için kısa tutuyoruz)
      const columnDelay = Math.random() * 1; // 0-1 saniye arası gecikme
      
      // Sütun içindeki karakterler için sabit hız - şerit etkisi için önemli
      const columnSpeed = 3 + Math.random() * 2; // 3-5 saniye arası
      
      // Her sütunda art arda düşen karakterleri oluştur
      for (let i = 0; i < charsPerColumn; i++) {
        // Karakter seçimi - %30 ihtimalle 0,1 rakamı, %70 ihtimalle Japon karakteri
        const useDigit = Math.random() < 0.3; // %30 ihtimalle rakam kullan
        
        let char;
        if (useDigit) {
          // 0 veya 1 seç
          char = Math.random() < 0.5 ? '0' : '1';
        } else {
          // Japon karakterlerinden birini seç (ilk 30 karakter)
          const japaneseIndex = Math.floor(Math.random() * (matrixSymbols.length - 2));
          char = matrixSymbols[japaneseIndex];
        }
        
        // Karakter gecikmesi - aynı sütunda ardışık düşme için
        // İlk karakterler hemen başlasın, sonrakiler kısa gecikmelerle
        const charDelay = columnDelay + (i * 0.1); // Ardışık gecikme
        
        // Parlaklık - Sütun başındaki karakterler daha parlak, iz bırakan efekt için
        const brightness = (i < 2) ? 1 : (i < 5 ? 0.8 : 0.6);
        
        chars.push({
          char: char,
          x: columnPosition, // Sütunlar sabit pozisyonda
          y: 0,
          delay: charDelay,
          speed: columnSpeed - (i === 0 ? 0.5 : 0), // Öncü karakterler biraz daha yavaş
          size: brightness > 0.9 ? 18 : 16, // Parlak olanlar biraz daha büyük
          brightness: brightness
        });
      }
    }
    
    setMatrixChars(chars);
    setMatrixActive(true);
    
    // 15 saniye sonra kapat
    setTimeout(() => {
      // Animasyonu yumuşak kapatmak için önce opacity'yi azalt
      const matrixContainer = document.querySelector('.matrix-container') as HTMLElement;
      if (matrixContainer) {
        matrixContainer.style.opacity = '0';
      }
      
      // Bir süre sonra tamamen kapat
      setTimeout(() => {
        setMatrixActive(false);
        setMatrixChars([]);
      }, 500);
    }, 15000);
  };
  
  // ALT+M tuş kombinasyonu ile Matrix modunu aktifleştir
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // ALT+M: Matrix modu
      if (e.altKey && e.key.toLowerCase() === 'm') {
        e.preventDefault();
        activateMatrixMode();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
  
  // Mobil cihazlar için uzun dokunma (5 saniye) ile Matrix modunu aktifleştir
  useEffect(() => {
    let longPressTimer: number | null = null;
    let startTime: number = 0;
    const requiredPressTime = 5000; // 5 saniye (ms cinsinden)
    
    // Dokunma başladığında zamanlayıcıyı başlat - gizli özellik
    const handleTouchStart = (e: TouchEvent) => {
      startTime = Date.now();
      
      // 5 saniye sonra tetiklenecek zamanlayıcı
      longPressTimer = window.setTimeout(() => {
        // Matrix modunu aktifleştir
        activateMatrixMode();
        longPressTimer = null;
      }, requiredPressTime);
    };
    
    // Dokunma sona erdiğinde zamanlayıcıyı iptal et
    const handleTouchEnd = () => {
      if (longPressTimer !== null) {
        clearTimeout(longPressTimer);
        longPressTimer = null;
      }
    };
    
    // Dokunma olaylarını dinle
    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchend', handleTouchEnd);
    document.addEventListener('touchcancel', handleTouchEnd);
    
    return () => {
      // Temizlik işlemleri
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchcancel', handleTouchEnd);
      
      if (longPressTimer !== null) {
        clearTimeout(longPressTimer);
      }
    };
  }, []);
  
  // Popup dışına tıklandığında kapat
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const popup = document.getElementById('settings-popup');
      const button = document.getElementById('settings-button');
      
      if (popup && button && !popup.contains(event.target as Node) && !button.contains(event.target as Node)) {
        setIsSettingsOpen(false);
      }
    };
    
    if (isSettingsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSettingsOpen]);
  
  // Tıklama imleçlerini gizlemek için useEffect
  useEffect(() => {
    // Tüm tıklama olaylarını yakalayıp seçimi engelleme
    const disableTextSelection = (e: MouseEvent) => {
      if (window.getSelection) {
        const selection = window.getSelection();
        if (selection) {
          if (selection.removeAllRanges) {
            selection.removeAllRanges();
          } else if (selection.empty) {
            selection.empty();
          }
        }
      }
    };

    // Tıklama olaylarını dinliyoruz
    document.addEventListener('mousedown', disableTextSelection);
    document.addEventListener('mouseup', disableTextSelection);
    document.addEventListener('click', disableTextSelection);
    
    // Component unmount olduğunda event listener'ları temizle
    return () => {
      document.removeEventListener('mousedown', disableTextSelection);
      document.removeEventListener('mouseup', disableTextSelection);
      document.removeEventListener('click', disableTextSelection);
    };
  }, []);
  
  if (!mounted) return null;
  
  return (
    <HomeContainer theme={theme as ThemeType}>
      <SEO />
      
      {/* Ayarlar butonu */}
      <SettingsButton 
        theme={theme as ThemeType} 
        onClick={() => setIsSettingsOpen(!isSettingsOpen)}
        id="settings-button"
        aria-label="Ayarlar"
      >
        ⚙️
      </SettingsButton>
      
      {/* Ayarlar popup */}
      <SettingsPopup 
        $isOpen={isSettingsOpen} 
        theme={theme as ThemeType}
        id="settings-popup"
      >
        <SettingsPopupTitle theme={theme as ThemeType}>
          {t('settings.title', 'Ayarlar')}
        </SettingsPopupTitle>
        
        <SettingsGroup>
          <SettingsGroupTitle theme={theme as ThemeType}>
            {t('settings.theme', 'Tema')}
          </SettingsGroupTitle>
          <SettingsSelect 
            theme={theme as ThemeType}
            onChange={handleThemeChange}
            value={theme}
          >
            <option value="cmd">Command Prompt</option>
            <option value="powershell">PowerShell</option>
            <option value="ubuntu">Ubuntu Terminal</option>
          </SettingsSelect>
        </SettingsGroup>
        
        <SettingsGroup>
          <SettingsGroupTitle theme={theme as ThemeType}>
            {t('settings.language', 'Dil')}
          </SettingsGroupTitle>
          <SettingsSelect 
            theme={theme as ThemeType}
            onChange={handleLanguageChange}
            value={i18n.language}
          >
            <option value="tr">Türkçe</option>
            <option value="en">English</option>
          </SettingsSelect>
        </SettingsGroup>
      </SettingsPopup>
      
      {/* Matrix Modu */}
      <MatrixContainer $active={matrixActive} className="matrix-container">
        {matrixChars.map((char, index) => (
          <MatrixChar 
            key={index}
            $delay={char.delay}
            $left={char.x}
            $speed={char.speed}
            $size={char.size}
            $brightness={char.brightness}
            className={matrixActive ? 'active' : ''}
          >
            {char.char}
          </MatrixChar>
        ))}
      </MatrixContainer>
      
      <ContentContainer>
        <LogoContainer className="terminal-cursor-container">
          <Logo theme={theme as ThemeType}>
            anıl<span>orhan</span>
            <TerminalCursor theme={theme as ThemeType} className="terminal-cursor" />
          </Logo>
          <LogoUnderline theme={theme as ThemeType} />
        </LogoContainer>
        
        <Tagline theme={theme as ThemeType}>
          {t('homePage.tagline', 'Yazılım Geliştirici & Çözüm Mimarı')}
        </Tagline>
        
        <OptionsContainer>
          <OptionButton 
            theme={theme as ThemeType}
            onClick={onOpenTerminal}
          >
            {t('homePage.openTerminal', 'Terminali Aç')}
          </OptionButton>
          
          <OptionButton 
            theme={theme as ThemeType}
            onClick={onOpenServices}
          >
            {t('homePage.goToServices', 'Hizmetleri Görüntüle')}
          </OptionButton>
        </OptionsContainer>
      </ContentContainer>
      
      <Footer theme={theme as ThemeType}>
        {t('footer.text', '© 2025 Anıl Orhan. Tüm hakları saklıdır.')}
        <br />
        {t('footer.easterEggHint', 'Alt + [?] kombinasyonu tahmin et ve Matrix modunu aktif et')}
      </Footer>
    </HomeContainer>
  );
};

// Gereksiz render'ları önlemek için memo ile sarmalama
const HomePage = React.memo(HomePageComponent);

export default HomePage; 